import { CookiePreferences, CONSENT_CHANGE_EVENT } from './cookie-preferences';
import './third-party-embeds.scss';

// Classe messa su <html> dallo script di guardia in testa all'head (partials/head.html,
// default_head_body.ftl) quando i contenuti di terze parti sono bloccati
const BLOCKED_CLASS = 'a70-terze-parti-bloccate';

// Servizi noti: nome mostrato, tipo di contenuto ("un video", "un post"...) e indirizzo del
// contenuto originale, entrambi ricavati dall'URL dell'embed (original: null se non ricavabile)
const SERVICES = [
  {
    hosts: ['youtube-nocookie.com', 'youtube.com', 'youtu.be'],
    name: 'YouTube',
    kind: () => 'un video',
    original: (url) => {
      const match = url.pathname.match(/^\/embed\/([\w-]+)/);
      return match ? `https://www.youtube.com/watch?v=${match[1]}` : null;
    },
  },
  {
    hosts: ['vimeo.com'],
    name: 'Vimeo',
    kind: () => 'un video',
    original: (url) => {
      const match = url.pathname.match(/^\/video\/(\d+)/);
      return match ? `https://vimeo.com/${match[1]}` : null;
    },
  },
  {
    hosts: ['spotify.com'],
    name: 'Spotify',
    kind: (url) => (/^\/embed\/(episode|show)\//.test(url.pathname) ? 'un podcast' : 'un contenuto audio'),
    original: (url) => (url.pathname.indexOf('/embed/') === 0 ? `https://open.spotify.com/${url.pathname.slice(7)}` : null),
  },
  {
    hosts: ['facebook.com'],
    name: 'Facebook',
    kind: (url) => (url.pathname.indexOf('/plugins/video') === 0 ? 'un video' : 'un post'),
    original: (url) => url.searchParams.get('href'),
  },
  {
    hosts: ['instagram.com'],
    name: 'Instagram',
    kind: (url) => (url.pathname.indexOf('/reel/') === 0 ? 'un video' : 'un post'),
    original: (url) => `https://www.instagram.com${url.pathname.replace(/\/embed\/?.*$/, '/')}`,
  },
  {
    hosts: ['twitter.com', 'x.com'],
    name: 'X',
    kind: () => 'un post',
    original: () => null,
  },
  {
    hosts: ['linkedin.com'],
    name: 'LinkedIn',
    kind: () => 'un post',
    original: (url) => `https://www.linkedin.com${url.pathname.replace(/^\/embed\//, '/')}`,
  },
  {
    hosts: ['google.com', 'google.it'],
    name: 'Google',
    kind: (url) => (url.pathname.indexOf('/maps') === 0 ? 'una mappa' : 'un contenuto'),
    original: () => null,
  },
];

// Post incorporati come blockquote, trasformati in embed dallo script del servizio. Il ripiego di
// Instagram è un riquadro vuoto con il solo link: si nasconde e al suo posto va il segnaposto. Quello
// di X contiene il testo del post: resta visibile, con l'avviso sopra
const SOCIAL_SCRIPTS = [
  {
    selector: 'blockquote.instagram-media',
    src: 'https://www.instagram.com/embed.js',
    host: 'instagram.com',
    permalink: (blockquote) => blockquote.getAttribute('data-instgrm-permalink'),
    keepFallback: false,
  },
  {
    selector: 'blockquote.twitter-tweet',
    src: 'https://platform.twitter.com/widgets.js',
    host: 'twitter.com',
    permalink: () => null,
    keepFallback: true,
  },
];

// Contenitori a proporzione fissa: con il segnaposto l'altezza segue il testo
const RATIO_WRAPPERS = '.video-wrapper, .ratio, .embed-responsive';

/**
 * true se hostname coincide con host o ne è un sottodominio
 */
function matchesHost(hostname, host) {
  return hostname === host || hostname.endsWith(`.${host}`);
}

/**
 * URL assoluto di un iframe, null se interno al sito o non http(s)
 */
function externalUrl(src) {
  if (!src) {
    return null;
  }
  try {
    const url = new URL(src, window.location.href);
    const isHttp = url.protocol === 'https:' || url.protocol === 'http:';
    return isHttp && url.hostname !== window.location.hostname ? url : null;
  } catch (e) {
    return null;
  }
}

/**
 * Servizio dell'URL: uno di SERVICES, o uno generico con il nome del dominio
 */
function serviceFor(url) {
  const known = SERVICES.find((service) => service.hosts.some((host) => matchesHost(url.hostname, host)));
  return known || {
    hosts: [url.hostname],
    name: url.hostname.replace(/^www\./, ''),
    kind: () => 'un contenuto',
    original: () => null,
  };
}

/**
 * Indirizzo del contenuto originale, solo https e sul dominio del servizio (niente javascript: o
 * domini diversi presi dai parametri dell'embed)
 */
function safeOriginal(service, candidate) {
  try {
    if (!candidate) {
      return null;
    }
    const original = new URL(candidate);
    const allowed = original.protocol === 'https:'
      && service.hosts.some((host) => matchesHost(original.hostname, host));
    return allowed ? original.href : null;
  } catch (e) {
    return null;
  }
}

/**
 * version    1.0
 * class      <type> (ThirdPartyEmbeds)
 * classdesc  Contenuti di terze parti incorporati (video, podcast, post social). Senza consenso il
 *            browser li blocca (CSP aggiunta dalla guardia in testa all'head) e qui si mettono i
 *            segnaposto; con il consenso si attivano gli script dei social, inerti nel markup
 */
export class ThirdPartyEmbeds {
  constructor() {
    this.init();
  }

  // private methods

  init() {
    this.blocked = document.documentElement.classList.contains(BLOCKED_CLASS);
    this.hasThirdPartyContent = false;

    if (this.blocked) {
      this.showPlaceholders();
    } else {
      this.activateScripts();
    }

    /**
     * Consenso dato senza ricaricare (banner o preferenze): la CSP della pagina non si può
     * allentare, quindi si ricarica se ci sono contenuti bloccati
     */
    document.addEventListener(CONSENT_CHANGE_EVENT, (event) => {
      if (this.blocked && this.hasThirdPartyContent && event.detail.thirdParty) {
        window.location.reload();
      }
    });

    /**
     * Pagina ripresa dalla cache "indietro/avanti" dopo un cambio di scelta in un'altra pagina
     */
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && this.hasThirdPartyContent
        && this.blocked === CookiePreferences.hasThirdPartyConsent()) {
        window.location.reload();
      }
    });
  }

  /**
   * Con consenso: carica gli script dei social (inerti nel markup con type="text/plain" e
   * data-a70-src) e quelli necessari ai blockquote incollati nel testo
   */
  activateScripts() {
    const sources = new Set();
    document.querySelectorAll('script[type="text/plain"][data-a70-src]').forEach((script) => {
      sources.add(script.getAttribute('data-a70-src'));
    });
    SOCIAL_SCRIPTS.forEach(({ selector, src, host }) => {
      const alreadyLoaded = Array.from(document.scripts)
        .some((script) => script.src && matchesHost(new URL(script.src).hostname, host));
      if (document.querySelector(selector) && !alreadyLoaded) {
        sources.add(src);
      }
    });
    sources.forEach((src) => {
      if (!externalUrl(src)) {
        return;
      }
      const script = document.createElement('script');
      script.async = true;
      script.src = src;
      document.body.appendChild(script);
    });
    this.hasThirdPartyContent = sources.size > 0
      || Array.from(document.querySelectorAll('iframe')).some((iframe) => externalUrl(iframe.getAttribute('src')));
  }

  /**
   * Senza consenso: ogni iframe esterno diventa un segnaposto; i post social incorporati come
   * blockquote hanno il segnaposto al posto del ripiego (Instagram) o un avviso sopra il testo (X)
   */
  showPlaceholders() {
    document.querySelectorAll('iframe').forEach((iframe) => {
      const url = externalUrl(iframe.getAttribute('src') || iframe.getAttribute('data-src'));
      if (!url) {
        iframe.classList.add('a70-iframe-interno');
        return;
      }
      const wrapper = iframe.parentElement;
      if (wrapper && wrapper.matches(RATIO_WRAPPERS)) {
        wrapper.classList.add('embed-consenso-contenitore');
      }
      const service = serviceFor(url);
      const message = `Qui ci sarebbe ${service.kind(url)} di ${service.name}.`;
      iframe.replaceWith(this.placeholder(service, message, safeOriginal(service, service.original(url))));
      this.hasThirdPartyContent = true;
    });

    SOCIAL_SCRIPTS.forEach((social) => {
      const {
        selector,
        src,
        permalink,
        keepFallback,
      } = social;
      document.querySelectorAll(selector).forEach((blockquote) => {
        const service = serviceFor(new URL(src));
        const message = keepFallback
          ? `Qui sotto c’è il testo di un post di ${service.name}, mostrato in versione semplice.`
          : `Qui ci sarebbe un post di ${service.name}.`;
        const original = keepFallback ? null : safeOriginal(service, permalink(blockquote));
        blockquote.before(this.placeholder(service, message, original));
        if (!keepFallback) {
          blockquote.setAttribute('hidden', '');
        }
        this.hasThirdPartyContent = true;
      });
    });
  }

  /**
   * Segnaposto: cosa ci sarebbe, perché serve il consenso, un pulsante per darlo e, se ricavabile, il
   * link al contenuto sul sito del servizio
   */
  placeholder(service, message, original) {
    const box = document.createElement('div');
    box.className = 'embed-consenso';

    const text = document.createElement('p');
    text.className = 'embed-consenso__testo';
    text.textContent = `${message} Per caricare il contenuto serve il tuo consenso ai cookie di terze`
      + ` parti: ${service.name} può raccogliere dati sulla tua navigazione.`;
    box.append(text);

    const actions = document.createElement('div');
    actions.className = 'embed-consenso__azioni';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-primary btn-xs embed-consenso__pulsante';
    button.textContent = 'Acconsenti e mostra';
    button.addEventListener('click', () => {
      CookiePreferences.setThirdPartyConsent(true);
      window.location.reload();
    });
    actions.append(button);
    if (original) {
      const link = document.createElement('a');
      link.className = 'embed-consenso__link';
      link.href = original;
      link.textContent = `Apri su ${service.name}`;
      actions.append(link);
    }
    box.append(actions);

    return box;
  }
}
